import React, { useCallback, useRef, useState } from "react"
import { useRouter } from "next/router"

import { useAgentTransportInvalidation } from "@/agents/transport"
import { SimulationWrapperType } from "@/test-set/simulation-wrappers"
import {
  isDuplicateTestSetNameError,
  mapCreateTestSetInputToTransport,
  mapDeleteTestSetInputToTransport,
  mapMediaUploadUrlInputToTransport,
  mapSaveTestSetEditorStateInputToTransport,
  mapSaveTestSetEditorStateResponse,
  useCreateTestSetTransport,
  useDeleteTestSetTransport,
  useGetMediaUploadUrlTransport,
  useInvalidateTestSetTransportQueries,
  useSaveTestSetEditorStateTransport,
} from "@/test-set/transport"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  toast,
} from "@coval/ui"
import { useQueryClient } from "@tanstack/react-query"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { invalidateHomeQueries } from "@/lib/home-query-cache"
import { logger } from "@/lib/logger"
import { useQueryParams } from "@/hooks/useQueryParams"
import type { MediaAttachment } from "@/types/TestCaseInput"

import { useTestSetEditorContext } from "../contexts/TestSetEditorContext"
import { useTestSetSaveState } from "../contexts/TestSetSaveContext"
import { EVENTS, useEventBus, useEventDispatcher } from "../event-bus"
import { useTestSetSimulationWrapperConfig } from "../hooks/useTestSetSimulationWrapperConfig"
import {
  applyCopilotDispatchAction,
  getCopilotDispatchAction,
} from "../utils/copilotSidebarState"
import { setCoviTestSetEditorSaveInFlight } from "../utils/coviTestSetEditorBridge"
import { getLatestUpdatedAt } from "../utils/testSetEditorContextState"
import {
  applyUploadedMediaAttachments,
  buildCreateTestSetInput,
  buildSaveMetadata,
  buildUploadedMediaAttachment,
  createMediaUploadPlan,
  getDiscardBehavior,
  hydrateSavedTestCases,
  remapLocalMediaAttachmentsByTestCaseId,
  remapPersistedTestCaseIds,
  resolveDeferredMediaUploads,
  resolveNextActiveAgentId,
  toSaveEditorStateTestCase,
  validateLocalMediaAttachment,
} from "../utils/testSetHeaderState"
import type { LocalMediaAttachment } from "./MediaAttachmentSection"
import { SimulationWrapperSelector } from "./SimulationWrapperSelector"
import { TestSetHeaderActions } from "./TestSetHeaderActions"

const log = logger("TestSetHeader")
const MEDIA_UPLOAD_TIMEOUT_MS = 15_000
const MAX_DUPLICATE_RETRIES = 10

export const TestSetHeader: React.FC = () => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const dispatch = useEventDispatcher()
  const { trackEvent } = useAnalytics()

  const {
    testSetId,
    metadata,
    hasChanges,
    testCases,
    selectedAgentIds,
    activeAgentId,
    pendingChanges,
    localMediaAttachmentsByTestCaseId,
    clearLocalMediaAttachments,
    featureFlags,
  } = useTestSetEditorContext()

  const {
    isSaving,
    setIsSaving,
    isSaveInFlight,
    registerApiSetupDraft,
    hasApiSetupDraft,
  } = useTestSetSaveState()
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false)
  const createdTestSetIdRef = useRef<string | null>(null)
  const createdTestCaseIdMapRef = useRef<Record<string, string>>({})
  const isSaveRequestInFlightRef = useRef(false)
  const isNewTestSet = queryParams.get("new") === "true"
  const isUnsavedNew = !testSetId
  const hasPending = (pendingChanges?.data?.length ?? 0) > 0
  const queryClient = useQueryClient()
  const invalidateTestSetTransportQueries =
    useInvalidateTestSetTransportQueries()
  const invalidateAgentTransportQueries = useAgentTransportInvalidation()
  const createTestSetMutation = useCreateTestSetTransport()
  const saveEditorStateMutation = useSaveTestSetEditorStateTransport()
  const getMediaUploadUrlMutation = useGetMediaUploadUrlTransport()
  const deleteTestSetMutation = useDeleteTestSetTransport()

  const {
    hasEnabledSimulationWrapper,
    handleSimulationWrapperChange,
    handleSimulationWrapperConfigChange,
    isSelectedSimulationWrapperEnabled,
    simulationWrapper,
    simulationWrapperConfig,
  } = useTestSetSimulationWrapperConfig()

  const handleCancel = () => {
    dispatch(EVENTS.CANCEL_REQUEST, {})
  }

  const handleDiscard = () => {
    setIsDiscardDialogOpen(true)
  }

  const handleConfirmDiscard = useCallback(async (): Promise<void> => {
    setIsDiscardDialogOpen(false)

    const slug = router.query.slug as string
    const listUrl = queryParams.getHref(`/test-sets`, slug)
    const discardBehavior = getDiscardBehavior({
      isUnsavedNew,
      listUrl,
      testCaseCount: testCases.length,
      testSetId,
    })

    if (discardBehavior.kind === "navigate") {
      trackEvent(
        discardBehavior.trackingEvent,
        discardBehavior.trackingProperties,
      )
      void router.push(discardBehavior.href)
      return
    }

    try {
      await deleteTestSetMutation.mutateAsync(
        mapDeleteTestSetInputToTransport({
          testSetId: discardBehavior.testSetId,
        }),
      )
      await Promise.all([
        invalidateHomeQueries(queryClient),
        invalidateTestSetTransportQueries(),
      ])
      trackEvent(
        discardBehavior.trackingEvent,
        discardBehavior.trackingProperties,
      )
      void router.push(discardBehavior.href)
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to delete test set. Please try again."
      log.error("Failed to delete test set", error)
      toast.danger({
        title: "Delete Failed",
        description: errorMessage,
      })
    }
  }, [
    router,
    queryParams,
    isUnsavedNew,
    deleteTestSetMutation,
    queryClient,
    invalidateTestSetTransportQueries,
    testSetId,
    testCases.length,
    trackEvent,
  ])

  const uploadLocalAttachment = async (
    testCaseId: string,
    attachment: LocalMediaAttachment,
  ): Promise<MediaAttachment> => {
    validateLocalMediaAttachment(attachment)

    const uploadInfo = await getMediaUploadUrlMutation.mutateAsync(
      mapMediaUploadUrlInputToTransport({
        testCaseId,
        filename: attachment.file.name,
        mimeType: attachment.file.type as MediaAttachment["mime_type"],
      }),
    )

    const uploadResponse = await fetch(uploadInfo.upload_url, {
      method: "PUT",
      headers: {
        "Content-Type": attachment.file.type,
      },
      body: attachment.file,
      signal: AbortSignal.timeout(MEDIA_UPLOAD_TIMEOUT_MS),
    })

    if (!uploadResponse.ok) {
      throw new Error(
        `Failed to upload media attachment (status ${uploadResponse.status}).`,
      )
    }

    return buildUploadedMediaAttachment({
      attachment,
      s3Bucket: uploadInfo.s3_bucket,
      s3Key: uploadInfo.s3_key,
    })
  }

  // Saving is the only accept step: staged Sofia rows are folded into the
  // editor and persisted in the same click.
  const acceptAndGetTestCases = () => {
    const action = getCopilotDispatchAction(pendingChanges)
    if (!action) {
      return testCases
    }
    switch (action.actionType) {
      case "EDIT":
        dispatch(EVENTS.COPILOT_UPDATE, { updates: action.updates })
        break
      case "ADD":
        dispatch(EVENTS.COPILOT_ADD, { testCases: action.testCases })
        break
      case "DELETE":
        dispatch(EVENTS.COPILOT_DELETE, { testCaseIds: action.testCaseIds })
        break
    }
    dispatch(EVENTS.PENDING_CHANGES_UPDATE, { changes: null })

    return applyCopilotDispatchAction(testCases, action)
  }

  const handleSave = async () => {
    if (isSaveInFlight() || (testCases.length === 0 && !hasPending)) {
      return
    }
    if (hasApiSetupDraft()) {
      toast.info("Apply or cancel API Setup before saving the test set.")
      return
    }
    const effectiveTestCases = hasPending ? acceptAndGetTestCases() : testCases
    setIsSaving(true)
    setCoviTestSetEditorSaveInFlight(true)

    try {
      let effectiveTestSetId = testSetId || createdTestSetIdRef.current
      let createdDisplayName: string | null = null

      if (!effectiveTestSetId) {
        let createMetadata = { ...metadata }
        let createResult: { success: boolean; testSetId?: string } | null = null

        for (let attempt = 0; attempt <= MAX_DUPLICATE_RETRIES; attempt++) {
          try {
            const transportCreateResult =
              await createTestSetMutation.mutateAsync(
                mapCreateTestSetInputToTransport({
                  testSetInput: buildCreateTestSetInput({
                    metadata: createMetadata,
                    testCases: effectiveTestCases,
                    includeTestCases: false,
                  }),
                }),
              )
            createResult = {
              success: transportCreateResult.success,
              testSetId: transportCreateResult.test_set_id,
            }
            await invalidateHomeQueries(queryClient)
            break
          } catch (createError: unknown) {
            if (
              !isDuplicateTestSetNameError(createError) ||
              attempt === MAX_DUPLICATE_RETRIES
            ) {
              throw createError
            }
            const baseName = createMetadata.displayName.replace(/ \(\d+\)$/, "")
            createMetadata = {
              ...createMetadata,
              displayName: `${baseName} (${attempt + 1})`,
            }
          }
        }

        if (!createResult?.success || !createResult.testSetId) {
          throw new Error("Failed to create test set")
        }

        if (createMetadata.displayName !== metadata.displayName) {
          dispatch(EVENTS.METADATA_UPDATE, {
            displayName: createMetadata.displayName,
          })
        }

        createdDisplayName = createMetadata.displayName
        effectiveTestSetId = createResult.testSetId
        createdTestSetIdRef.current = createResult.testSetId

        trackEvent(TRACKING_EVENTS.TEST_SET.CREATED, {
          method: "editor",
          test_set_id: createResult.testSetId,
          test_case_count: effectiveTestCases.length,
        })

        // Transition to persisted route immediately so retries don't create
        // duplicate test sets if downstream save/upload steps fail.
        const slug =
          typeof router.query.slug === "string" ? router.query.slug : ""
        const baseUrl = queryParams.getHref(
          `/test-sets/${effectiveTestSetId}`,
          slug,
        )
        void router.replace(baseUrl)
      }

      if (!effectiveTestSetId) {
        throw new Error("Failed to resolve test set id for save.")
      }

      const saveMetadata = buildSaveMetadata(
        createdDisplayName !== null
          ? { ...metadata, displayName: createdDisplayName }
          : metadata,
      )
      const resolvedTestSetId = effectiveTestSetId
      const persistedCreatedIdMap = createdTestCaseIdMapRef.current
      const normalizedTestCases = remapPersistedTestCaseIds(
        effectiveTestCases,
        persistedCreatedIdMap,
      )
      const mediaUploadPlan = createMediaUploadPlan({
        testCases: normalizedTestCases,
        localMediaAttachmentsByTestCaseId:
          remapLocalMediaAttachmentsByTestCaseId({
            localMediaAttachmentsByTestCaseId,
            createdIdMap: persistedCreatedIdMap,
          }),
      })
      const immediateUploadedAttachments = await Promise.all(
        mediaUploadPlan.immediateUploads.map(
          async ({ testCaseId, attachment }) => ({
            testCaseId,
            attachment: await uploadLocalAttachment(testCaseId, attachment),
          }),
        ),
      )
      const firstPassTestCases = applyUploadedMediaAttachments({
        testCases: mediaUploadPlan.baseTestCases,
        uploadedAttachments: immediateUploadedAttachments,
      })

      let activeSaveMetadata = { ...saveMetadata }
      let result: Awaited<
        ReturnType<typeof mapSaveTestSetEditorStateResponse>
      > | null = null

      for (let attempt = 0; attempt <= MAX_DUPLICATE_RETRIES; attempt++) {
        try {
          result = mapSaveTestSetEditorStateResponse(
            await saveEditorStateMutation.mutateAsync(
              mapSaveTestSetEditorStateInputToTransport({
                testSetId: resolvedTestSetId,
                metadata: activeSaveMetadata,
                testCases: firstPassTestCases.map(toSaveEditorStateTestCase),
                selectedAgentIds,
              }),
            ),
          )
          break
        } catch (saveError: unknown) {
          if (
            !isDuplicateTestSetNameError(saveError) ||
            attempt === MAX_DUPLICATE_RETRIES
          ) {
            throw saveError
          }
          const baseName = activeSaveMetadata.displayName.replace(
            / \(\d+\)$/,
            "",
          )
          activeSaveMetadata = {
            ...activeSaveMetadata,
            displayName: `${baseName} (${attempt + 1})`,
          }
        }
      }

      if (!result) {
        throw new Error("Failed to save test set")
      }

      if (activeSaveMetadata.displayName !== saveMetadata.displayName) {
        dispatch(EVENTS.METADATA_UPDATE, {
          displayName: activeSaveMetadata.displayName,
        })
      }
      createdTestCaseIdMapRef.current = {
        ...createdTestCaseIdMapRef.current,
        ...(result.createdIdMap ?? {}),
      }

      if (mediaUploadPlan.deferredUploads.length > 0) {
        const persistedCases = hydrateSavedTestCases(result.testCases || [])
        const deferredUploadRequests = resolveDeferredMediaUploads({
          createdIdMap: createdTestCaseIdMapRef.current,
          deferredUploads: mediaUploadPlan.deferredUploads,
        })
        const deferredUploadedAttachments = await Promise.all(
          deferredUploadRequests.map(async ({ testCaseId, attachment }) => ({
            testCaseId,
            attachment: await uploadLocalAttachment(testCaseId, attachment),
          })),
        )
        const secondPassTestCases = applyUploadedMediaAttachments({
          testCases: persistedCases,
          uploadedAttachments: deferredUploadedAttachments,
        })

        result = mapSaveTestSetEditorStateResponse(
          await saveEditorStateMutation.mutateAsync(
            mapSaveTestSetEditorStateInputToTransport({
              testSetId: resolvedTestSetId,
              metadata: activeSaveMetadata,
              testCases: secondPassTestCases.map(toSaveEditorStateTestCase),
              selectedAgentIds,
            }),
          ),
        )
      }

      const hydratedTestCases = hydrateSavedTestCases(result.testCases || [])
      const nextSelectedAgentIds = [...selectedAgentIds]
      const nextActiveAgentId = resolveNextActiveAgentId({
        activeAgentId,
        selectedAgentIds: nextSelectedAgentIds,
      })

      // Re-hydrate editor state from server truth (only now do we clear "unsaved changes")
      const testSetMetadata = result.testSet.test_set_metadata || {}
      dispatch(EVENTS.HYDRATE_FROM_SERVER, {
        metadata: {
          displayName: result.testSet.display_name,
          description: result.testSet.description || "",
          createdAt: result.testSet.created_at,
          lastUpdatedAt: getLatestUpdatedAt(
            result.testSet.last_updated_at ?? undefined,
            result.testCases || [],
          ),
          createdByUserId: result.testSet.created_by_id,
          simulationWrapperType:
            (testSetMetadata.simulationWrapperType as SimulationWrapperType | null) ||
            null,
        },
        testCases: hydratedTestCases,
        selectedAgentIds: nextSelectedAgentIds,
        activeAgentId: nextActiveAgentId,
      })

      // Refresh shared caches used elsewhere in the app
      await Promise.all([
        invalidateTestSetTransportQueries(),
        invalidateAgentTransportQueries(),
      ])

      clearLocalMediaAttachments()
      createdTestCaseIdMapRef.current = {}

      if (!isUnsavedNew && !isNewTestSet) {
        trackEvent(TRACKING_EVENTS.TEST_SET.SAVED, {
          test_set_id: resolvedTestSetId,
          test_case_count: testCases.length,
        })
      }

      if (isUnsavedNew) {
        const slug =
          typeof router.query.slug === "string" ? router.query.slug : ""
        const baseUrl = queryParams.getHref(
          `/test-sets/${resolvedTestSetId}`,
          slug,
        )
        void router.replace(baseUrl)

        const fromAgentId = queryParams.get("fromAgentId")
        if (fromAgentId && slug) {
          toast.success({
            title: "Test set created",
            duration: 8000,
            action: {
              label: "Go back to agent",
              onClick: () => {
                queryParams.push(
                  `/agents/${encodeURIComponent(fromAgentId)}`,
                  slug,
                )
              },
            },
          })
        }

        return
      }

      // Clear ?new=true so the UI transitions to normal "existing test set" behavior
      if (isNewTestSet) {
        queryParams.setMultiple(
          { new: null },
          { history: "replace", shallow: true },
        )

        const fromAgentId = queryParams.get("fromAgentId")
        const slug =
          typeof router.query.slug === "string" ? router.query.slug : ""
        if (fromAgentId && slug) {
          toast.success({
            title: "Test set saved",
            duration: 8000,
            action: {
              label: "Go back to agent",
              onClick: () => {
                queryParams.push(
                  `/agents/${encodeURIComponent(fromAgentId)}`,
                  slug,
                )
              },
            },
          })
        }
      }
    } catch (error) {
      log.error("Failed to update test set", error)
      if (isUnsavedNew) {
        trackEvent(TRACKING_EVENTS.TEST_SET.CREATION_FAILED, {
          method: "editor",
          error: error instanceof Error ? error.message : "Unknown error",
        })
      }
      toast.error({
        title: isUnsavedNew ? "Failed to create test set" : "Failed to save",
        error,
        fallback: "Could not save your test set. Please try again.",
      })
    } finally {
      setIsSaving(false)
      setCoviTestSetEditorSaveInFlight(false)
    }
  }

  // The Sofia panel's tray sits outside this header, so it commits staged rows
  // by asking for the same save. Gate matches the header button's disabled
  // state; HYDRATE_FROM_SERVER already resets the editor, so no SAVE_COMPLETE.
  useEventBus(EVENTS.SAVE_REQUEST, () => {
    const isEmpty = testCases.length === 0 && !hasPending
    if (isSaving || isEmpty || isSaveRequestInFlightRef.current) {
      return
    }
    isSaveRequestInFlightRef.current = true
    void handleSave().finally(() => {
      isSaveRequestInFlightRef.current = false
    })
  })

  // Mirrors what TestSetHeaderActions renders, so the strip above the platter
  // collapses entirely rather than leaving a band of empty canvas.
  const hasHeaderStripContent =
    hasEnabledSimulationWrapper ||
    isNewTestSet ||
    isUnsavedNew ||
    hasChanges ||
    hasPending

  return (
    <>
      {hasHeaderStripContent && (
        <div className="shrink-0 w-full px-6 pb-4">
          <div className="flex flex-wrap items-center justify-end gap-3">
            {hasEnabledSimulationWrapper && (
              <div className="flex max-w-full flex-wrap items-center gap-3">
                {featureFlags.isCustomWrapperObservationsEnabled && (
                  <SimulationWrapperSelector
                    value={
                      metadata.simulationWrapperType ===
                      SimulationWrapperType.CUSTOM
                        ? SimulationWrapperType.CUSTOM
                        : undefined
                    }
                    onChange={handleSimulationWrapperChange}
                    showCustomOption
                    disabled={isSaving}
                  />
                )}
                {isSelectedSimulationWrapperEnabled && simulationWrapper && (
                  <>
                    {simulationWrapper.renderTopLevelConfig(
                      simulationWrapperConfig,
                      (config) => {
                        if (isSaveInFlight()) {
                          toast.info(
                            "Wait for Save to finish. Your API Setup draft is preserved.",
                          )
                          return false
                        }
                        handleSimulationWrapperConfigChange(config)
                        return true
                      },
                      isSaving,
                      registerApiSetupDraft,
                    )}
                  </>
                )}
              </div>
            )}

            <TestSetHeaderActions
              hasChanges={hasChanges}
              hasPending={hasPending}
              isNewTestSet={isNewTestSet}
              isSaving={isSaving}
              isUnsavedNew={isUnsavedNew}
              onCancel={handleCancel}
              onDiscard={handleDiscard}
              onSave={() => {
                void handleSave()
              }}
              testCaseCount={testCases.length}
            />
          </div>
        </div>
      )}

      <AlertDialog
        open={isDiscardDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsDiscardDialogOpen(false)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Test Set</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to discard this test set? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button
                variant="ghost"
                onClick={() => setIsDiscardDialogOpen(false)}
              >
                Cancel
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                variant="danger"
                onClick={() => {
                  void handleConfirmDiscard()
                }}
              >
                Discard
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
